// dependencies.
import { useEffect, useState, forwardRef } from 'react'
import styled from 'styled-components'

// components.
import { Label } from '../Typography'
// utils.
import { useDimensions } from '../../js/hooks'
import { theme } from '../../styleguide'

/*********************************/
/*                               */
/*    Development Home Screen    */
/*                               */
/*********************************/

// partials

const TabHeader = styled.div(
  {
    alignItems: 'stretch',
    backgroundColor: theme.colors.white,
    borderBottom: `1px solid ${theme.colors.ash}`,
    borderTop: `1px solid ${theme.colors.ash}`,
    display: 'flex',
    flexDirection: 'row',
    gap: 0,
    margin: 0,
    padding: 0,
    width: '100%',
  },
  `@media print { display: none; }`
)

const StyledItem = styled(Label)((props) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  boxSizing: 'border-box',
  minHeight: props.isMobile ? 52 : 0,
  padding: props.isMobile ? '8px 4px 6px' : '13px 16px 9px',
  borderBottom: props.isActive
    ? `4px solid ${theme.colors.primary}`
    : '4px solid transparent',
  transition: 'all 200ms ease',
  fontSize: props.isMobile ? '13px' : 'inherit',
  textAlign: 'center',
  minWidth: 0,
  width: props.isMobile ? `${100 / 3}%` : 'fit-content',
  ...(props.isMobile
    ? {
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'clip',
        lineHeight: 1.2,
      }
    : {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
  '& .tab-count': {
    fontSize: '11px',
  },
  '& .tab-ult': {
    fontSize: props.isMobile ? '9px' : '10px',
    fontWeight: 400,
    opacity: props.isMobile ? 0.7 : 0.75,
    lineHeight: props.isMobile ? 1.1 : 1.2,
  },
  '& .tab-ult-desk': {
    fontSize: '9px',
    opacity: 0.65,
  },
  '& .tab-stack': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: props.isMobile ? 0 : 1,
    maxWidth: '100%',
  },
  '& .tab-stack-title, & .tab-line-nowrap': {
    lineHeight: 1.15,
    whiteSpace: 'nowrap',
  },
}))

const TabBody = styled.div({
  height: '100%',
  overflow: 'hidden',
})

const Row = styled.div({
  display: 'flex',
  height: '100%',
})

// main component.
const Tabs = forwardRef(({ items, activeTabIndex }, ref) => {
  // hooks.
  const { isMobile } = useDimensions()
  // states.
  const [activeTab, setActiveTab] = useState(0)

  useEffect(() => {
    if (
      typeof activeTabIndex === 'number' &&
      activeTabIndex >= 0 &&
      activeTabIndex < items.length
    ) {
      setActiveTab(activeTabIndex)
    }
  }, [activeTabIndex, items.length])

  // ------------------------------------------------------------------------------------------- //
  // CONTENT                                                                                     //
  // ------------------------------------------------------------------------------------------- //

  if (!items.length) return null

  const resolveLabel = (label, isActive) =>
    typeof label === 'function' ? label(isActive) : label

  // return content.
  return (
    <>
      <TabHeader isMobile={isMobile}>
        {items.map(({ label }, i) => (
          <StyledItem
            key={i}
            isActive={activeTab === i}
            isMobile={isMobile}
            color={activeTab === i ? 'black' : 'grey'}
            onClick={() => setActiveTab(i)}
          >
            {resolveLabel(label, activeTab === i)}
          </StyledItem>
        ))}
      </TabHeader>

      <TabBody ref={ref}>
        {items.map(({ component }, i) => (activeTab === i ? <Row key={i}>{component}</Row> : null))}
      </TabBody>
    </>
  )
})

export default Tabs
